import { decode } from 'he';
import { NextPageContext } from 'next';
import router from 'next/router';
import { ReactElement, useCallback, useEffect } from 'react';
import { postStoreEventWeb } from '../../../api/store/postStoreEventWeb';
import { SCHEME_CLOSE_WEBVIEW } from '../../../common/constant';
import { TrackPageViewContainer } from '../../../common/containers/TrackPageViewContainer';
import { getEnv } from '../../../common/env/env';
import { t } from '../../../common/getTranslations';
import { redirectTo } from '../../../common/redirectTo';
import { requestContext } from '../../../common/requestContext';
import { ApiStatusCodeVo } from '../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import { convertQueryInputToSingleElement } from '../../../common/shared/utils/convertQueryInputToSingleElement';
import { Layout } from '../../../design-system/layout/Layout';
import { Dialog } from '../../../design-system/organisms/Dialog';
import { useDialog } from '../../../dialog/useDialog.hook';
import { AdditionallyExpectedApiStatusCodes, EVENT_END_MESSAGE } from '../../../domain/event/[hashedeventId]/constant';
import { EventPageV2Container } from '../../../domain/event/[hashedeventId]/container/EventPageV2Container';
import { EventPageV3Container } from '../../../domain/event/[hashedeventId]/container/EventPageV3Container';
import { Event, EventVersionType } from '../../../domain/event/[hashedeventId]/type';
import { getSerializedPagewebError } from '../../../errors/getSerializedPagewebError.function';
import { createGetEventInfoPayload } from '../../../graphql/factory/GetEventInfoPayload';
import { GetEventInfoPayload } from '../../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../../header/constants';
interface Query {
  hashedeventId?: string;
  target?: string;
  eventResponse?: GetEventInfoPayload;
  type?: string;
  viewtype?: string;
}
interface EventPageProps {
  eventResponse: Event;
  hashedEventId: string;
  type?: string;
  viewType?: string;
}
function EventPage({
  eventResponse: {
    eventVersion,
    contentInfoList,
    isNeedUpdate,
    isAlert,
    alertMessage,
    eventTitle
  },
  hashedEventId
}: EventPageProps) {
  const contentUrl = getEnv().kageUrlPrefix;
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const {
    DialogWrapper,
    showDialog
  } = useDialog({
    isMobile
  });
  const onClickErrorDialogConfirm = useCallback(() => {
    if (isWebView) {
      window.location.assign(SCHEME_CLOSE_WEBVIEW);
    } else {
      // 웹에서 보여질 페이지가 아니라 router.back 동작을 임시로 넣어놨습니다.
      router.back();
    }
  }, [isWebView]);
  useEffect(() => {
    if (isAlert && alertMessage === EVENT_END_MESSAGE) {
      showDialog();
    }
  }, [isAlert, alertMessage, showDialog]);

  // 이벤트가 종료 or 참여 불가능한 경우 내용을 보여주지 않고 alert만 노출된다
  if (isAlert && alertMessage === EVENT_END_MESSAGE) {
    return <DialogWrapper isMobile={isMobile}>
        <Dialog.Dimmed />
        <Dialog.Title>{alertMessage}</Dialog.Title>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickErrorDialogConfirm}>
            {t('common_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>;
  }
  let toBeRenderedComponent;
  switch (eventVersion) {
    case EventVersionType.V2:
      toBeRenderedComponent = <>
          <EventPageV2Container contentUrl={contentUrl} isAlert={isAlert} isNeedUpdate={!!isNeedUpdate} contentInfoList={contentInfoList} alertMessage={alertMessage} />
          <TrackPageViewContainer trackObj={{
          section: '이벤트페이지',
          page: '이벤트페이지',
          actionType: 'Pageview',
          actionName: '이벤트페이지_화면',
          page_meta: {
            id: hashedEventId,
            type: 'eventId',
            name: eventTitle
          },
          customProps: {
            user_action: 'pv'
          }
        }} />
        </>;
      break;
    case EventVersionType.V3:
      toBeRenderedComponent = <EventPageV3Container hashedEventId={hashedEventId} eventTitle={eventTitle} />;
      // V3 는 내부에서 알아서 PV 를 진행합니다
      break;
    // 이벤트 정보가 없거나(만료), V3 이벤트지만 에러 메시지는 보여주고 싶을 때 어쩔 수 없이 EventPageV2 컴포넌트를 이용해서 보여준다
    default:
      toBeRenderedComponent = <>
          <EventPageV2Container contentUrl={contentUrl} isAlert={true} isNeedUpdate={!!isNeedUpdate} contentInfoList={contentInfoList} alertMessage={alertMessage || EVENT_END_MESSAGE} />
          <TrackPageViewContainer trackObj={{
          section: '이벤트페이지',
          page: '이벤트페이지',
          actionType: 'Pageview',
          actionName: '이벤트페이지_화면',
          page_meta: {
            id: hashedEventId,
            type: 'eventId',
            name: eventTitle
          },
          customProps: {
            user_action: 'pv'
          }
        }} />
        </>;
  }
  return <>{toBeRenderedComponent}</>;
}
EventPage.getInitialProps = async ({
  query,
  req,
  res
}: NextPageContext): Promise<EventPageProps | {}> => {
  const {
    hashedeventId: _hashedeventId,
    target: _target,
    eventResponse,
    type: _type,
    viewtype: _viewtype
  } = query as Query;
  const hashedEventId = convertQueryInputToSingleElement(_hashedeventId);
  const target = convertQueryInputToSingleElement(_target);
  const type = convertQueryInputToSingleElement(_type);
  const viewType = convertQueryInputToSingleElement(_viewtype);
  // node.js 에서 이미 API 통해 정보를 가져왔다면 중복 패치를 방지하기 위해 그대로 리턴한다.
  if (eventResponse) {
    return {
      eventResponse,
      hashedEventId
    };
  }
  const {
    isWebView: isWebInApp,
    isMobile
  } = requestContext.requestInfo.display;
  if (!hashedEventId) {
    redirectTo('/main', res, true);
    return {};
  }
  let data;
  let error;
  let resultCode;
  try {
    const result = await postStoreEventWeb({
      hashedEventId,
      target,
      isWebInApp,
      isMobile
    }, {
      ssrRequest: req
    });
    data = {
      getEventInfo: createGetEventInfoPayload(result)
    };
  } catch (e) {
    const pagewebError = getSerializedPagewebError(e);
    resultCode = pagewebError.resultCode;
    error = pagewebError;
  }
  if (!error || resultCode !== undefined && AdditionallyExpectedApiStatusCodes.includes(resultCode)) {
    let isAlert = false;
    let alertMessage;
    if (resultCode === ApiStatusCodeVo.EventNotFound || resultCode === ApiStatusCodeVo.EventClosed) {
      isAlert = true;
      alertMessage = EVENT_END_MESSAGE;
    }
    return {
      eventResponse: {
        isAlert,
        alertMessage,
        ...data?.getEventInfo
      },
      hashedEventId,
      type,
      viewType
    } as EventPageProps;
  } else {
    redirectTo('/404', res, true);
    return {};
  }
};
EventPage.getLayout = function getLayout(page: ReactElement) {
  const eventResponse = page.props.eventResponse as Event;
  const eventTitle = eventResponse.eventTitle ? decode(eventResponse.eventTitle) : undefined;
  const eventVersion = eventResponse.eventVersion ?? EventVersionType.V2;
  const type = page.props.type;
  const viewType = page.props.viewType;
  return <Layout title={eventTitle || t('landing_event')} mobileLayoutConfig={{
    // 헬릭스 랜딩페이지이며 viewType이 full일 경우에만 헤더를 숨겨줍니다.
    shouldShowHeader: !(type === 'helix' && viewType === 'full'),
    shouldShowFooter: eventVersion === EventVersionType.V3,
    headerType: CommonMobileHeaderType.TwoDepth
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: eventVersion === EventVersionType.V3
  }}>
      {page}
    </Layout>;
};
export default EventPage;